import { useGetDuringAndPostExamSettingsFromExamCourseSettingsByExamCourseCodeQuery } from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  styled,
  PaperSection,
  CircleIcon,
  Divider,
  Flex,
  SectionTitle,
  Grid,
  Box,
  Text,
  FormRadioSelection,
  CheckCircleOutlineRoundedIcon,
  FormSelection,
  FormNumberInput,
  ConfirmationModal,
  CancelOutlinedIcon,
  Form,
  FormLabel,
  FormFileInput,
  Button,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ACCEPT_ATTACH_DOCUMENT_FILE_TYPES, QUESTIONNAIRE_TEMPLATE_OPTIONS } from '../../constants';
import {
  useGetDuringAndPostExamSettingsFormOptions,
  useDuringAndPostExamSettingsFormLogic,
  useDuringAndPostExamSettingsFormSubmission,
} from '../../hooks/';
import { type TemplateValue } from '../../utils/certificate-templates';
import { FailCertificateTemplatePreviewer } from '../fail-certificate-template-previewer';
import { PassCertificateTemplatePreviewer } from '../pass-certificate-template-previewer';

import { DuringAndPostExamSettingsSectionFormSchema } from './schema';
import {
  type DuringAndPostExamSettingsSectionFormOutputs,
  type DuringAndPostExamSettingsSectionFormInputs,
} from './types';

interface DuringAndPostExamSettingsSectionFormProps {
  examCourseId: string;
  examCourseCode: string;
  onFormDirtyChange?: (isDirty: boolean) => void;
}

const StyledCard = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(4),
  gap: theme.spacing(6),
  gridTemplateColumns: '1fr 1fr',
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.rounded(4),
  border: `1px solid ${theme.palette.grey[300]}`,
  width: '100%',
}));

export default function DuringAndPostExamSettingsSectionForm(
  props: Readonly<DuringAndPostExamSettingsSectionFormProps>,
) {
  const { examCourseId, examCourseCode, onFormDirtyChange } = props;

  const { showPromiseSnackbar } = useSnackbar();
  const [isExamCourseSettingsConfirmationModalOpen, examCourseSettingsConfirmationModalHandlers] =
    useDisclosure();
  const {
    open: openExamCourseSettingsConfirmationModal,
    close: closeExamCourseSettingsConfirmationModal,
  } = examCourseSettingsConfirmationModalHandlers;

  const { data: examCourseData } =
    useGetDuringAndPostExamSettingsFromExamCourseSettingsByExamCourseCodeQuery({
      variables: {
        getExamCourseByExamCourseCodeInput: {
          examCourseCode,
        },
      },
      skip: !examCourseCode,
    });

  const examCourse = examCourseData?.ecGetExamCourseByExamCourseCode.examCourse;

  const { passCertificateTemplates, failCertificateTemplates } =
    useGetDuringAndPostExamSettingsFormOptions(examCourse?.type, examCourse?.language);

  const formContext = useForm<
    DuringAndPostExamSettingsSectionFormInputs,
    unknown,
    DuringAndPostExamSettingsSectionFormOutputs
  >({
    resolver: zodResolver(DuringAndPostExamSettingsSectionFormSchema),
  });

  const isFormDirty = formContext.formState.isDirty;

  useEffect(() => {
    onFormDirtyChange?.(isFormDirty);
  }, [isFormDirty, onFormDirtyChange]);

  const { disabledFields, disabledOptions, visibleFields, passTemplate, failTemplate } =
    useDuringAndPostExamSettingsFormLogic({
      form: formContext,
      examCourse,
    });

  const onSubmit = () => {
    openExamCourseSettingsConfirmationModal();
  };

  const { handleFormSubmit, isSubmitting } = useDuringAndPostExamSettingsFormSubmission({
    examCourseId,
    examCourseCode,
    onCompleted: () => {
      closeExamCourseSettingsConfirmationModal();
    },
  });

  const handleConfirm = () => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึก',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'ไม่สามารถบันทึกข้อมูลได้',
      },
      promise: () => handleFormSubmit(formContext.getValues()),
    });
  };

  const handleCancel = () => {
    closeExamCourseSettingsConfirmationModal();
  };

  return (
    <>
      <FormProvider {...formContext}>
        <Form onSubmit={formContext.handleSubmit(onSubmit)}>
          <PaperSection title='During & Post-Exam Settings'>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                p: theme.spacing(6),
                gap: theme.spacing(6),
              })}
            >
              <SectionTitle title='Result Display Settings' />
              <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
                <Box>
                  <Text variant='body1Semibold'>การแสดงผลสอบ (ผ่าน/ไม่ผ่าน)</Text>
                </Box>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  <FormRadioSelection
                    name='resultDisplay.shouldDisplay'
                    options={[
                      {
                        label: 'ไม่แสดงผลสอบ',
                        value: 'false',
                      },
                      {
                        label: 'แสดงผลสอบ',
                        value: 'true',
                      },
                    ]}
                  />
                </Flex>
              </Grid>
              <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
                <Box>
                  <Text variant='body1Semibold'>การส่งผลสอบไปยัง SET Portal</Text>
                </Box>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  <FormRadioSelection
                    name='resultSend.portal'
                    options={[
                      {
                        label: 'ไม่ส่งผลสอบ',
                        value: 'false',
                      },
                      {
                        label: 'ส่งผลสอบ',
                        value: 'true',
                        disabled: disabledOptions?.['resultSend.portal.true'],
                      },
                    ]}
                  />
                </Flex>
              </Grid>
              <Divider />
              <SectionTitle title='Certificate Settings' />
              <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
                <Box>
                  <Text variant='body1Semibold'>ออกใบรับรองหรือไม่</Text>
                </Box>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  <FormRadioSelection
                    name='certificate.shouldIssue'
                    options={[
                      {
                        label: 'ไม่ออกใบรับรอง',
                        value: 'false',
                      },
                      {
                        label: 'ออกใบรับรอง',
                        value: 'true',
                        disabled: disabledOptions?.['certificate.shouldIssue.true'],
                      },
                    ]}
                  />
                  {visibleFields?.condition ? (
                    <Flex
                      sx={{
                        pl: (theme) => theme.spacing(8),
                        flexDirection: 'column',
                        gap: (theme) => theme.spacing(4),
                      }}
                    >
                      <StyledCard>
                        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                          <Flex sx={{ alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
                            <CheckCircleOutlineRoundedIcon
                              sx={{ color: (theme) => theme.palette.success.main }}
                            />
                            <Text variant='body1Semibold'>Template - Pass</Text>
                          </Flex>
                          <FormSelection
                            name='certificate.template.passTemplate'
                            label='เลือก Certificate Template'
                            placeholder='เลือก Certificate Template'
                            required
                            options={passCertificateTemplates ?? []}
                          />
                        </Flex>
                        <PassCertificateTemplatePreviewer
                          examCourseType={examCourse?.type}
                          examCourseLanguage={examCourse?.language}
                          variant={passTemplate as TemplateValue}
                        />
                      </StyledCard>
                      <StyledCard>
                        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                          <Flex sx={{ alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
                            <CancelOutlinedIcon
                              sx={{ color: (theme) => theme.palette.error.main }}
                            />
                            <Text variant='body1Semibold'>Template - Fail</Text>
                          </Flex>
                          <FormSelection
                            name='certificate.template.failTemplate'
                            label='เลือก Certificate Template'
                            placeholder='เลือก Certificate Template'
                            required
                            options={failCertificateTemplates ?? []}
                          />
                        </Flex>
                        <FailCertificateTemplatePreviewer
                          examCourseType={examCourse?.type}
                          examCourseLanguage={examCourse?.language}
                          variant={failTemplate as TemplateValue}
                        />
                      </StyledCard>
                      <Flex sx={{ gap: (theme) => theme.spacing(4), flexDirection: 'column' }}>
                        <FormRadioSelection
                          label='ระยะเวลาที่ส่ง Certificate'
                          row
                          name='certificate.deliveryTime.type'
                          required
                          options={[
                            {
                              value: 'immediately',
                              label: 'ส่งทันที',
                              disabled:
                                disabledOptions?.['certificate.deliveryTime.type.immediately'],
                            },
                            {
                              value: 'delay',
                              label: (
                                <Flex
                                  sx={{
                                    alignItems: 'center',
                                    gap: (theme) => theme.spacing(2),
                                    flexWrap: 'nowrap',
                                  }}
                                >
                                  <Text sx={{ flexShrink: 0 }}>ส่งหลัง</Text>
                                  <FormNumberInput
                                    name='certificate.deliveryTime.delay'
                                    disabled={disabledFields?.['certificate.deliveryTime.delay']}
                                    sx={{ maxWidth: '80px' }}
                                    slotProps={{ htmlInput: { sx: { textAlign: 'center' } } }}
                                  />
                                  <Text sx={{ flexShrink: 0 }}>วัน</Text>
                                </Flex>
                              ),
                            },
                          ]}
                        />
                        {visibleFields?.averageScoreCondition ? (
                          <FormRadioSelection
                            label='เงื่อนไขการคำนวณคะแนนเฉลี่ย'
                            name='certificate.averageScoreCondition'
                            required
                            options={[
                              {
                                label: 'คำนวณรวมทุกศูนย์สอบ',
                                value: 'all-centers',
                              },
                              {
                                label: 'คำนวณแต่ละศูนย์สอบ',
                                value: 'each-center',
                              },
                            ]}
                          />
                        ) : null}
                      </Flex>
                    </Flex>
                  ) : null}
                </Flex>
              </Grid>
              {visibleFields?.condition ? (
                <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
                  <Box>
                    <Text variant='body1Semibold'>อายุของใบรับรอง</Text>
                  </Box>
                  <FormRadioSelection
                    name='certificate.expiry.type'
                    options={[
                      {
                        label: 'ไม่มีวันหมดอายุ',
                        value: 'none',
                      },
                      {
                        label: (
                          <Flex
                            sx={{
                              alignItems: 'center',
                              gap: (theme) => theme.spacing(2),
                              flexWrap: 'nowrap',
                            }}
                          >
                            <Text sx={{ flexShrink: 0 }}>หมดอายุหลังจาก</Text>
                            <FormNumberInput
                              name='certificate.expiry.delay'
                              disabled={disabledFields?.['certificate.expiry.delay']}
                              sx={{ maxWidth: '80px' }}
                              slotProps={{ htmlInput: { sx: { textAlign: 'center' } } }}
                            />
                            <Text sx={{ flexShrink: 0 }}>ปี (นับจากวันที่สอบผ่าน)</Text>
                          </Flex>
                        ),
                        value: 'delay',
                        disabled: disabledOptions['certificate.expiry.type.delay'],
                      },
                    ]}
                  />
                </Grid>
              ) : null}
              <Divider />
              <SectionTitle title='Questionnaire Settings' />
              <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
                <Box>
                  <Text variant='body1Semibold'>มีแบบสอบถามหรือไม่</Text>
                </Box>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  <FormRadioSelection
                    name='questionnaire.enabled'
                    options={[
                      {
                        label: 'ไม่มีแบบสอบถาม',
                        value: 'false',
                      },
                      {
                        label: 'มีแบบสอบถาม',
                        value: 'true',
                      },
                    ]}
                  />
                  {visibleFields?.questionnaireTemplate ? (
                    <Flex sx={{ pl: (theme) => theme.spacing(8) }}>
                      <FormSelection
                        name='questionnaire.templateId'
                        label='เลือก Questionnaire Template'
                        placeholder='เลือก Questionnaire Template'
                        required
                        options={QUESTIONNAIRE_TEMPLATE_OPTIONS}
                      />
                    </Flex>
                  ) : null}
                </Flex>
              </Grid>
              <SectionTitle title='Exam Attachment Settings' />
              <Grid sx={{ gap: (theme) => theme.spacing(6), gridTemplate: '1fr / 240px 1fr' }}>
                <Box>
                  <Text variant='body1Semibold'>มีเอกสารประกอบการสอบหรือไม่</Text>
                </Box>
                <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                  <FormRadioSelection
                    name='attachDocument.enabled'
                    options={[
                      {
                        label: 'ไม่มีเอกสาร',
                        value: 'false',
                      },
                      {
                        label: 'มีเอกสาร',
                        value: 'true',
                      },
                    ]}
                  />
                </Flex>
                {visibleFields?.attachDocument ? (
                  <>
                    <FormLabel required sx={(theme) => ({ ...theme.typography.body1Semibold })}>
                      อัปโหลดเอกสารแนบ
                    </FormLabel>
                    <FormFileInput
                      required
                      name='attachDocument.file'
                      placeholder='กรุณาอัปโหลดไฟล์ .PDF'
                      accept={ACCEPT_ATTACH_DOCUMENT_FILE_TYPES}
                      helperText='สามารถอัปโหลดได้สูงสุด 1 ไฟล์ (ขนาดไม่เกิน 20 MB)'
                    />
                  </>
                ) : null}
              </Grid>
            </Flex>
            <Divider />
            <Flex
              sx={(theme) => ({
                p: theme.spacing(4, 6),
                gap: theme.spacing(4),
                alignItems: 'center',
                justifyContent: 'right',
              })}
            >
              <Flex
                sx={{
                  flexDirection: 'column',
                  gap: (theme) => theme.spacing(2),
                  alignItems: 'flex-end',
                }}
              >
                <Text variant='caption'>การเปลี่ยนแปลงมีผลกับรอบสอบในวันถัดไป</Text>
                {isFormDirty ? (
                  <Text variant='caption' color='text.secondary'>
                    <CircleIcon
                      sx={{
                        fontSize: '8px',
                        color: (theme) => theme.palette.primary.main,
                      }}
                    />{' '}
                    มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก
                  </Text>
                ) : null}
              </Flex>
              <Button type='submit' disabled={!isFormDirty} loading={isSubmitting}>
                บันทึก
              </Button>
            </Flex>
          </PaperSection>
        </Form>
      </FormProvider>
      <ConfirmationModal
        open={isExamCourseSettingsConfirmationModalOpen}
        title='บันทึกการตั้งค่า?'
        content={
          <Text>
            การเปลี่ยนแปลงจะมีผลกับ{' '}
            <Text component='span' variant='body1Semibold'>
              “รอบสอบวันถัดไป”
            </Text>{' '}
            โดยอัตโนมัติ
          </Text>
        }
        onAccept={handleConfirm}
        onClose={handleCancel}
        onDiscard={handleCancel}
        loading={isSubmitting}
        acceptText='บันทึก'
        discardText='ยกเลิก'
      />
    </>
  );
}
